import { CommandBlock } from "@/components/CommandBlock";
import { SetupStep } from "@/components/SetupStep";
import { formatDate } from "@/lib/formatDate";
import { formatRelativeTime } from "@/lib/formatRelativeTime";
import {
  downloadCommandFor,
  installCommandFor,
  verifyCommandFor,
} from "@/lib/installCommands";
import { useCopyToClipboard } from "@/lib/useCopyToClipboard";

interface EnrollmentProps {
  entityType: "connector" | "gateway";
  createdAt: string;
  lastSeenAt: string | null;
}

export function EnrollmentSteps({
  entityType,
  createdAt,
  lastSeenAt,
}: EnrollmentProps) {
  const { copiedField, copyToClipboard } = useCopyToClipboard();

  const downloadCommand = downloadCommandFor(entityType);
  const verifyCommand = verifyCommandFor(entityType);
  const installCommand = installCommandFor("<token>");

  return (
    <ol className="list-none">
      <SetupStep
        label="1"
        state="done"
        title={`${entityType === "gateway" ? "Gateway" : "Connector"} created`}
      >
        <p className="mt-0.5 text-sm text-foreground/70">
          Token issued {formatDate(createdAt)}.
        </p>
      </SetupStep>

      <SetupStep
        label="2"
        state={lastSeenAt ? "done" : "current"}
        title="Install on the host"
      >
        <p className="mt-0.5 mb-2 text-sm text-foreground/70">
          Download the binary.
        </p>
        <CommandBlock
          command={downloadCommand}
          field="downloadCommand"
          copiedField={copiedField}
          onCopy={copyToClipboard}
          className="mb-4"
        />
        <p className="mb-2 text-sm text-foreground/70">
          <span className="mr-2 rounded-full bg-muted px-2 py-0.5 text-xs text-foreground/70">
            optional
          </span>
          Verify that the binary was built by Tendra's release pipeline and has
          not been modified since. Requires{" "}
          <a
            className="underline underline-offset-2 hover:text-foreground"
            href="https://docs.sigstore.dev/cosign/system_config/installation/"
            target="_blank"
            rel="noreferrer"
          >
            cosign
          </a>
          .
        </p>
        <CommandBlock
          command={verifyCommand}
          field="verifyCommand"
          copiedField={copiedField}
          onCopy={copyToClipboard}
          className="mb-4"
        />
        <p className="mb-2 text-sm text-foreground/70">
          {`Run this command as root to install the ${entityType}. Replace <token> with the token shown when this ${entityType} was created.`}
        </p>
        <CommandBlock
          command={installCommand}
          field="installCommand"
          copiedField={copiedField}
          onCopy={copyToClipboard}
          className=""
        />
      </SetupStep>

      <SetupStep
        label="3"
        state={lastSeenAt ? "done" : "todo"}
        title={lastSeenAt ? "Connected" : "Waiting for first connection..."}
        last
      >
        <p className="mt-0.5 text-sm text-foreground/70">
          {lastSeenAt
            ? `Last checked in ${formatRelativeTime(lastSeenAt)}.`
            : `This page will refresh automatically when the ${entityType} checks in.`}
        </p>
      </SetupStep>
    </ol>
  );
}

export function EnrollmentChecklist(props: EnrollmentProps) {
  return (
    <div className="rounded-lg bg-surface p-4">
      <EnrollmentSteps {...props} />
    </div>
  );
}
